import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Plus, Trash, PencilSimple, IdentificationBadge } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Funcionarios() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const empty = { name: "", email: "", password: "", role: "funcionario", valor_hora: 0, telefone: "", cargo: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/funcionarios");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, valor_hora: parseFloat(form.valor_hora) || 0 };
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.put(`/funcionarios/${editing}`, payload);
      } else {
        await api.post("/funcionarios", payload);
      }
      toast.success(t("common.success"));
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const openEdit = (u) => {
    setEditing(u.id);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      valor_hora: u.valor_hora || 0,
      telefone: u.telefone || "",
      cargo: u.cargo || "",
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("?")) return;
    try {
      await api.delete(`/funcionarios/${id}`);
      toast.success(t("common.success"));
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="funcionarios-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("funcionarios.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("funcionarios.subtitle")}</p>
        </div>

        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="add-funcionario-btn">
              <Plus size={18} weight="bold" /> {t("funcionarios.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? t("funcionarios.edit") : t("funcionarios.add")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t("funcionarios.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="func-name-input" />
              </div>
              <div>
                <Label>{t("funcionarios.email")}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="func-email-input" />
              </div>
              <div>
                <Label>
                  {t("funcionarios.password")}
                  {editing && <span className="text-xs text-slate-400 ml-2">({t("funcionarios.leaveBlank")})</span>}
                </Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} data-testid="func-password-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("funcionarios.role")}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="func-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="funcionario">{t("funcionarios.employee")}</SelectItem>
                      <SelectItem value="admin">{t("funcionarios.admin")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("funcionarios.hourRate")}</Label>
                  <Input type="number" step="0.01" min="0" value={form.valor_hora} onChange={(e) => setForm({ ...form, valor_hora: e.target.value })} data-testid="func-hourrate-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("funcionarios.phone")}</Label>
                  <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
                </div>
                <div>
                  <Label>{t("funcionarios.position")}</Label>
                  <Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("funcionarios.cancel")}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="func-save-btn">{t("funcionarios.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <IdentificationBadge size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("funcionarios.empty")}
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("funcionarios.name")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("funcionarios.email")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("funcionarios.role")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("funcionarios.hourRate")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("funcionarios.position")}</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`func-row-${u.id}`}>
                  <td className="px-4 py-3 text-sm text-slate-900 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <Badge className={u.role === "admin" ? "bg-red-100 text-red-800 border-0" : "bg-green-100 text-green-800 border-0"}>
                      {u.role === "admin" ? t("funcionarios.admin") : t("funcionarios.employee")}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">€ {(u.valor_hora || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{u.cargo || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-func-${u.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    {u.role !== "admin" && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(u.id)} data-testid={`delete-func-${u.id}`}>
                        <Trash size={16} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
